import React, { useState, useEffect } from 'react';
import { FaHistory, FaUser, FaDesktop, FaGlobe, FaShieldAlt } from 'react-icons/fa';
import API_BASE_URL from '../../config/api';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 10;

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/get_activity_log.php`);
      const data = await response.json();
      if (data.status === 'success') {
        setLogs(data.data);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [pin, setPin] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinError, setPinError] = useState(false);
  const CORRECT_PIN = "778899";

  useEffect(() => {
    if (isAuthenticated) {
      fetchLogs();
    }
  }, [isAuthenticated]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setIsAuthenticated(true);
    } else {
      setPinError(true);
      setPin('');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center py-20 h-full">
        <div className="bg-white p-8 rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl shadow-inner border border-slate-100">
            <FaShieldAlt />
          </div>
          <h2 className="text-xl font-black text-slate-800 mb-2">Security Verification</h2>
          <p className="text-sm font-medium text-slate-500 mb-6 leading-relaxed">Please enter the master PIN to view activity logs.</p>
          
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div>
              <input 
                type="password" 
                value={pin}
                onChange={(e) => { setPin(e.target.value); setPinError(false); }}
                placeholder="Enter PIN"
                className={`w-full px-4 py-3 bg-slate-50 border ${pinError ? 'border-red-300 focus:border-red-500 text-red-600' : 'border-slate-100 focus:border-orange-500 text-slate-800'} rounded-xl outline-none font-black text-center text-xl tracking-[0.5em] transition-colors`}
                autoFocus
              />
              {pinError && <p className="text-xs font-bold text-red-500 mt-2 uppercase tracking-widest">Incorrect PIN</p>}
            </div>
            <button type="submit" className="w-full py-3.5 bg-slate-900 text-white font-black rounded-xl hover:bg-black transition-colors uppercase tracking-widest text-sm shadow-md">
              Verify Access
            </button>
          </form>
        </div>
      </div>
    );
  }

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = logs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(logs.length / logsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <FaHistory className="text-orange-500" /> System Activity History
        </h2>
        <button onClick={fetchLogs} className="text-xs font-bold text-orange-500 hover:underline uppercase tracking-widest">Refresh Logs</button>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <th className="px-6 py-4">Timestamp</th>
                <th className="px-6 py-4">User / Role</th>
                <th className="px-6 py-4">Action Taken</th>
                <th className="px-6 py-4">IP & Device Info</th>
                <th className="px-6 py-4">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center">
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-400 font-medium italic">No activity logs found.</td>
                </tr>
              ) : currentLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="text-xs font-bold text-slate-700">{new Date(log.created_at).toLocaleDateString()}</span>
                      <span className="text-[10px] text-slate-400 font-bold">{new Date(log.created_at).toLocaleTimeString()}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${log.role === 'admin' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                        {log.full_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">
                          {log.full_name || 'Guest'} 
                          <span className="ml-2 text-[10px] text-slate-400 font-normal">#{log.user_id?.toString().padStart(4, '0')}</span>
                        </p>
                        <p className="text-[9px] font-black uppercase text-slate-400 tracking-tighter">{log.role || 'Guest'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest">
                      {log.action}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500">
                        <FaGlobe size={10} className="text-slate-300" /> {log.ip_address}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 truncate max-w-[150px]" title={log.user_agent}>
                        <FaDesktop size={10} className="text-slate-300" /> {log.user_agent}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-xs text-slate-600 italic font-medium">{log.details}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-t border-slate-50 bg-slate-50/50 gap-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              Showing {indexOfFirstLog + 1} to {Math.min(indexOfLastLog, logs.length)} of {logs.length} entries
            </p>
            <div className="flex gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg font-bold text-[10px] uppercase tracking-widest disabled:opacity-50 hover:bg-slate-50 transition-colors shadow-sm"
              >
                Previous
              </button>
              <div className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg font-black text-[10px] uppercase tracking-widest shadow-inner border border-orange-100">
                Page {currentPage} / {totalPages}
              </div>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-lg font-bold text-[10px] uppercase tracking-widest disabled:opacity-50 hover:bg-slate-50 transition-colors shadow-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
